import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import api, { downloadCsv } from '../api/client';
import { useAuth } from '../context/AuthContext';
import { Spinner, ErrorBox, Status, Risk, BudgetStatus, BudgetBar, DataTable,
  DropdownMenu, PrintHeader, fmtDate, num, money } from '../components/ui';

const COLS = {
  'building-type-breakdown': [
    { key:'programme_name', label:'Programme' },
    { key:'programme_type', label:'Type', render:v=><span className={`badge ${v}`}>{v}</span> },
    { key:'project_count', label:'Projects', render:num },
    { key:'hrb_buildings', label:'HRB buildings', render:num },
    { key:'lrb_buildings', label:'LRB buildings', render:num }
  ],
  'outstanding-work': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'stage_name', label:'Stage' }, { key:'substage_name', label:'Task' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'Expected end date', render:fmtDate },
    { key:'status_name', label:'Status', render:v=><Status value={v}/> },
    { key:'days_overdue', label:'Days over', render:num },
    { key:'assigned_name', label:'Assigned' }
  ],
  'who-does-what': [
    { key:'assigned_name', label:'Person' }, { key:'assigned_org', label:'Organisation' },
    { key:'project_name', label:'Project' }, { key:'stage_name', label:'Stage' },
    { key:'substage_name', label:'Task' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'Expected end date', render:fmtDate },
    { key:'status_name', label:'Status', render:v=><Status value={v}/> }
  ],
  'programme-progress': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'Expected end date', render:fmtDate },
    { key:'progress_status', label:'Status', render:v=><Status value={v}/> },
    { key:'risk_level', label:'Risk', render:v=><Risk value={v}/> },
    { key:'complete_substages', label:'Complete', render:num },
    { key:'applicable_substages', label:'Applicable', render:num },
    { key:'pct_by_count', label:'% by count', render:v=>`${num(v)}%` },
    { key:'pct_by_duration', label:'% by duration', render:v=>`${num(v)}%` },
    { key:'delayed_substages', label:'Delayed', render:num }
  ],
  'projects-at-risk': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'Expected end date', render:fmtDate },
    { key:'days_to_expected_end', label:'Days left', render:v=>v==null?'—':Number(v)<0?`${Math.abs(v)} past`:num(v) },
    { key:'risk_level', label:'Risk', render:v=><Risk value={v}/> },
    { key:'progress_status', label:'Status', render:v=><Status value={v}/> },
    { key:'pct_by_count', label:'Progress', render:v=>`${num(v)}%` },
    { key:'delayed_substages', label:'Delayed tasks', render:num }
  ],
  'budget-tracking': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'budget_amount', label:'Budgeted', render:money },
    { key:'total_spent', label:'Total spent', render:money },
    { key:'balance', label:'Balance', render:(v)=><span className={Number(v)<0?'neg':''}>{money(v)}</span> },
    { key:'pct_spent', label:'Spent', render:(v,r)=><BudgetBar pct={v} status={r.budget_status}/> },
    { key:'budget_status', label:'Budget status', render:v=><BudgetStatus value={v}/> },
    { key:'item_count', label:'Items', render:num }
  ],
  'stage-completion': [
    { key:'stage_code', label:'Stage' }, { key:'stage_name', label:'Name' },
    { key:'projects_with_stage', label:'Projects', render:num },
    { key:'completed', label:'Completed', render:num },
    { key:'in_progress', label:'In progress', render:num },
    { key:'delayed', label:'Delayed', render:num },
    { key:'outstanding', label:'Outstanding', render:num },
    { key:'not_started', label:'Not started', render:num }
  ],
  'timeliness': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'substage_name', label:'Task' }, { key:'expected_end_date', label:'Expected end', render:fmtDate },
    { key:'completed_on', label:'Done on', render:fmtDate },
    { key:'finish_variance_days', label:'Variance (d)', render:num },
    { key:'outcome', label:'Outcome' }
  ],
  'contractor-workload': [
    { key:'organisation', label:'Organisation' },
    { key:'total_tasks', label:'Total tasks', render:num },
    { key:'complete', label:'Completed', render:num },
    { key:'in_progress', label:'In progress', render:num },
    { key:'outstanding', label:'Outstanding', render:num },
    { key:'delayed', label:'Delayed', render:num },
    { key:'next_due', label:'Next due', render:fmtDate }
  ],
  'gateway-tracker': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'gateway2_submission', label:'Gateway 2 submission', render:v=>v?<Status value={v}/>:'—' },
    { key:'gateway2_approval', label:'Gateway 2 approval', render:v=>v?<Status value={v}/>:'—' },
    { key:'building_control', label:'Building Control (LRB)', render:v=>v||'—' },
    { key:'gateway3_certificate', label:'Gateway 3 cert (HRB)', render:v=>v||'—' }
  ],
  'due-soon': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'stage_name', label:'Stage' }, { key:'substage_name', label:'Task' },
    { key:'building_name', label:'Building', render:v=>v||'—' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'Expected end date', render:fmtDate },
    { key:'status_name', label:'Status', render:v=><Status value={v}/> },
    { key:'assigned_name', label:'Assigned', render:(v,r)=>v?`${v}${r.assigned_org?' · '+r.assigned_org:''}`:'Unassigned' }
  ],
  'tasks': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project (Site)' },
    { key:'stage_name', label:'Stage' }, { key:'substage_name', label:'Task' },
    { key:'building_name', label:'Building', render:v=>v||'—' },
    { key:'start_date', label:'Start date', render:fmtDate },
    { key:'expected_end_date', label:'End date', render:fmtDate },
    { key:'actual_start', label:'Actually started', render:fmtDate },
    { key:'completed_on', label:'Completed on', render:fmtDate },
    { key:'status_name', label:'Status', render:v=><Status value={v}/> },
    { key:'assigned_name', label:'Assigned', render:v=>v||'Unassigned' }
  ],
  'submission-status': [
    { key:'programme_name', label:'Programme' },
    { key:'complete', label:'Complete', render:num },
    { key:'outstanding', label:'Outstanding', render:num },
    { key:'total', label:'Total', render:num }
  ],
  'buildings-register': [
    { key:'programme_name', label:'Programme' }, { key:'project_name', label:'Project' },
    { key:'building_name', label:'Building' },
    { key:'building_type', label:'Type', render:v=><span className={`badge ${v}`}>{v}</span> },
    { key:'storeys', label:'Storeys', render:num },
    { key:'units', label:'Units', render:num }
  ]
};

export default function Reports() {
  const { can } = useAuth();
  const [params] = useSearchParams();
  const { key: routeKey } = useParams();
  const [catalogue, setCatalogue] = useState([]);
  const [selected, setSelected] = useState(routeKey || params.get('open') || 'building-type-breakdown');
  const [report, setReport] = useState(null);
  const [err, setErr] = useState(null); const [busy, setBusy] = useState(false);

  useEffect(() => { api.get('/reports').then(r=>setCatalogue(r.data)).catch(setErr); }, []);
  useEffect(() => { if (routeKey) setSelected(routeKey); }, [routeKey]);

  const run = useCallback(async () => {
    setBusy(true); setErr(null); setReport(null);
    try {
      const extra = {};
      if (params.get('overdueOnly')) extra.overdueOnly = params.get('overdueOnly');
      if (params.get('state')) extra.state = params.get('state');
      const r = await api.get(`/reports/${selected}`, { params: extra });
      setReport(r.data);
    } catch (e) { setErr(e); } finally { setBusy(false); }
  }, [selected, params]);
  useEffect(() => { if (catalogue.length) run(); }, [selected, catalogue.length, run]);
  useEffect(() => {
    const h = () => run();
    window.addEventListener('bsp2-prog-change', h);
    return () => window.removeEventListener('bsp2-prog-change', h);
  }, [run]);

  const def = catalogue.find(r => r.key === selected);

  // Group the catalogue by category for the dropdown menu.
  const menuGroups = Object.entries(
    catalogue.reduce((acc, r) => { (acc[r.category || 'Other'] ||= []).push(r); return acc; }, {})
  ).sort(([a], [b]) => a.localeCompare(b))
   .map(([label, items]) => ({
     label,
     items: items.map(r => ({ value: r.key, label: r.title, hint: r.question }))
   }));
  // Use the explicit column map if defined; otherwise derive columns from
  // the returned rows so a new backend report never renders blank.
  const autoCols = (report?.rows?.length)
    ? Object.keys(report.rows[0]).map(k => ({
        key: k,
        label: k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        render: /_(on|date|end|start|due)$|^completed_on$|_at$/.test(k) ? fmtDate : undefined
      }))
    : null;
  const cols = COLS[selected] || autoCols;

  return <>
    <div className="phead no-print"><h1>Reports</h1>
      <div style={{display:'flex',gap:8}}>
        {can('report.export') && report && <button onClick={()=>downloadCsv(`/reports/${selected}/export`,{},`${selected}.csv`).catch(setErr)}>Export CSV</button>}
        <button onClick={()=>window.print()}>Print / PDF</button>
      </div>
    </div>
    <div className="panel no-print">
      <div className="filters">
        {/* A plain dropdown menu rather than a select box: the reports are
            grouped by category and each carries a line of description,
            neither of which a native select can show. */}
        <DropdownMenu label="Report" value={selected} groups={menuGroups} onChange={setSelected} />
      </div>
      {def && <><p style={{marginBottom:4,fontWeight:600,marginTop:12}}>{def.question}</p><p className="muted" style={{marginBottom:0}}>{def.description}</p></>}
    </div>
    <ErrorBox error={err} />
    {busy && <Spinner label="Running report…" />}
    {report && <>
      <PrintHeader title={report.title} subtitle={report.question} generatedAt={report.generatedAt} generatedBy={report.generatedBy} />
      {report.summary && <div className="panel"><h3>Summary</h3>
        <div className="stat-grid" style={{marginBottom:0}}>
          {Object.entries(report.summary).map(([k,v])=>(
            <div className="stat static" key={k}>
              <div className="label">{k.replace(/_/g,' ')}</div>
              <div className="value" style={/budget|spent|balance/.test(k)?{fontSize:19}:undefined}>
                {/budget(ed)?$|spent|balance/.test(k) ? money(v) : num(v)}
              </div>
            </div>))}
        </div></div>}
      {report.items && report.items.length > 0 && <div className="panel"><h3>Expenditure items</h3>
        <DataTable rows={report.items} columns={[
          {key:'project_name',label:'Project'},
          {key:'item_date',label:'Date',render:fmtDate},
          {key:'description',label:'Description of the item'},
          {key:'amount',label:'Amount',render:money},
          {key:'reference',label:'Reference',render:v=>v||'—'},
          {key:'created_by_name',label:'Created by',render:v=>v||'—'}
        ]} /></div>}
      {report.byProject && <div className="panel"><h3>Projects by type</h3>
        <DataTable rows={report.byProject} columns={[{key:'project_type',label:'Type'},{key:'projects',label:'Projects',render:num}]} /></div>}
      {report.byBuilding && <div className="panel"><h3>Buildings by type</h3>
        <DataTable rows={report.byBuilding} columns={[{key:'building_type',label:'Type'},{key:'buildings',label:'Buildings',render:num},{key:'projects',label:'Projects',render:num}]} /></div>}
      {report.byPerson && <div className="panel"><h3>By person</h3>
        <DataTable rows={report.byPerson} columns={[{key:'assigned_name',label:'Person'},{key:'assigned_org',label:'Organisation'},{key:'tasks',label:'Tasks',render:num},{key:'delayed',label:'Delayed',render:num},{key:'next_due',label:'Next due',render:fmtDate}]} /></div>}
      {cols && <div className="panel page-break"><div className="phead"><h3>{report.title}</h3><span className="muted">{report.rows?.length||0} rows</span></div>
        <DataTable columns={cols} rows={report.rows} /></div>}
      <div className="print-footer">Building Safety Programme Tracker · {report.title} · Generated {report.generatedAt ? new Date(report.generatedAt).toLocaleString('en-GB') : ''}</div>
    </>}
  </>;
}
